import React, { useEffect, useState, memo, useCallback } from 'react';
import {
  Box,
  VStack,
  Text,
  Badge,
  HStack,
  IconButton,
  useColorModeValue,
  Icon,
  Tooltip,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import { FaComment, FaPhone, FaTasks, FaExclamationTriangle, FaExclamationCircle, FaClock } from 'react-icons/fa';
import { SettingsIcon, InfoIcon } from '@chakra-ui/icons';
import RecordCallModal from './RecordCallModal';
import { useFontSize } from '../../contexts/FontSizeContext';

import api from '../../services/api';
import { useSocket } from '../../contexts/SocketContext';

const LeadCard = ({ lead, onEdit, onAdvancedEdit }) => {
  const { socket } = useSocket();
  const toast = useToast();
  const { fontSize } = useFontSize();
  const { isOpen: isCallModalOpen, onOpen: onCallModalOpen, onClose: onCallModalClose } = useDisclosure();

  // State to track the local call count
  const [localCallCount, setLocalCallCount] = useState(Number(lead.callCount || 0));
  const [localLastCallTime, setLocalLastCallTime] = useState(lead.lastCallTime);
  // State to track the local next follow-up time
  const [localNextFollowUpTime, setLocalNextFollowUpTime] = useState(lead.nextFollowUpTime);
  
  // State to force re-render when nextFollowUpTime changes
  const [renderKey, setRenderKey] = useState(Date.now());

  // Keep local state in sync with lead prop
  useEffect(() => {
    setLocalCallCount(Number(lead.callCount || 0));
    setLocalLastCallTime(lead.lastCallTime);
    
    // Update local next follow-up time if it's different from the prop
    if (lead.nextFollowUpTime !== localNextFollowUpTime) {
      console.log('[LeadCard] Syncing local follow-up time with prop:', {
        leadId: lead.leadId,
        propTime: lead.nextFollowUpTime,
        localTime: localNextFollowUpTime,
        formattedProp: lead.nextFollowUpTime ? new Date(lead.nextFollowUpTime).toLocaleString() : 'none',
        formattedLocal: localNextFollowUpTime ? new Date(localNextFollowUpTime).toLocaleString() : 'none',
        time: new Date().toLocaleTimeString()
      });
      setLocalNextFollowUpTime(lead.nextFollowUpTime);
    }
  }, [lead.callCount, lead.lastCallTime, lead.nextFollowUpTime, localNextFollowUpTime, lead.leadId]);

  // Define handleLeadUpdated function wrapped in useCallback to avoid recreation on each render
  const handleLeadUpdated = useCallback((updatedLead) => {
    if (updatedLead.leadId === lead.leadId) {
      console.log('[LeadCard] Received lead:updated event:', {
        leadId: updatedLead.leadId,
        callCount: updatedLead.callCount,
        lastCallTime: updatedLead.lastCallTime,
        nextFollowUpTime: updatedLead.nextFollowUpTime,
        currentPropsNextFollowUpTime: lead.nextFollowUpTime,
        timeReceived: new Date().toLocaleTimeString()
      });
      
      // Compare all relevant fields between current props and update
      console.log('[LeadCard] Comparing props vs update:', {
        leadId: lead.leadId,
        callCount: { current: lead.callCount, update: updatedLead.callCount, different: lead.callCount !== updatedLead.callCount },
        nextFollowUpTime: { 
          current: lead.nextFollowUpTime, 
          update: updatedLead.nextFollowUpTime, 
          different: lead.nextFollowUpTime !== updatedLead.nextFollowUpTime,
          formattedCurrent: lead.nextFollowUpTime ? new Date(lead.nextFollowUpTime).toLocaleString() : 'none',
          formattedUpdate: updatedLead.nextFollowUpTime ? new Date(updatedLead.nextFollowUpTime).toLocaleString() : 'none'
        },
        timeCompared: new Date().toLocaleTimeString()
      });
      
      // Update call count if it's different
      if (updatedLead.callCount !== undefined) {
        const newCount = Number(updatedLead.callCount);
        if (newCount !== localCallCount) {
          console.log('[LeadCard] Updating call count from server:', {
            leadId: updatedLead.leadId,
            oldCount: localCallCount,
            newCount: newCount,
            timeUpdated: new Date().toLocaleTimeString()
          });
          setLocalCallCount(newCount);
        }
      }
      
      // Update last call time if it's available
      if (updatedLead.lastCallTime) {
        setLocalLastCallTime(updatedLead.lastCallTime);
      }

      // Log follow-up time updates (using lead data directly now)
      if (updatedLead.nextFollowUpTime !== undefined) {
        console.log('[LeadCard] Received follow-up time update from server:', {
          leadId: updatedLead.leadId,
          nextFollowUpTime: updatedLead.nextFollowUpTime,
          currentPropsNextFollowUpTime: lead.nextFollowUpTime,
          currentLocalNextFollowUpTime: localNextFollowUpTime,
          formattedNew: updatedLead.nextFollowUpTime ? new Date(updatedLead.nextFollowUpTime).toLocaleString() : 'none',
          formattedCurrent: lead.nextFollowUpTime ? new Date(lead.nextFollowUpTime).toLocaleString() : 'none',
          timeUpdated: new Date().toLocaleTimeString(),
          willRerender: lead.nextFollowUpTime !== updatedLead.nextFollowUpTime || localNextFollowUpTime !== updatedLead.nextFollowUpTime
        });
        
        // Update local follow-up time state if it's different
        if (localNextFollowUpTime !== updatedLead.nextFollowUpTime) {
          console.log('[LeadCard] Updating local follow-up time:', {
            leadId: updatedLead.leadId,
            old: localNextFollowUpTime,
            new: updatedLead.nextFollowUpTime,
            formattedOld: localNextFollowUpTime ? new Date(localNextFollowUpTime).toLocaleString() : 'none',
            formattedNew: updatedLead.nextFollowUpTime ? new Date(updatedLead.nextFollowUpTime).toLocaleString() : 'none'
          });
          setLocalNextFollowUpTime(updatedLead.nextFollowUpTime);
        }
        
        // If we got a follow-up time update via socket, force refresh from API
        if (lead.nextFollowUpTime !== updatedLead.nextFollowUpTime) {
          console.log('[LeadCard] Follow-up time changed, fetching fresh data from API');
          
          // Fetch the latest lead data from the API to ensure we have all fields
          (async () => {
            try {
              const response = await api.get(`/api/leads/${lead.leadId}`);
              console.log('[LeadCard] Received updated lead data from API:', {
                leadId: response.data.leadId,
                nextFollowUpTime: response.data.nextFollowUpTime,
                formattedTime: response.data.nextFollowUpTime ? new Date(response.data.nextFollowUpTime).toLocaleString() : 'none'
              });
              
              // Force the parent component to re-render with the new data
              if (response.data.nextFollowUpTime !== lead.nextFollowUpTime) {
                // Emit a lead:updated event with the fresh data to ensure all components update
                socket.emit('lead:updated', response.data);
              }
            } catch (error) {
              console.error('[LeadCard] Error fetching updated lead data:', error);
            }
          })();
        }
      }
    }
  }, [lead.leadId, lead.nextFollowUpTime, lead.callCount, localCallCount, localNextFollowUpTime, socket]);
  
  // Set up event listener for lead updates
  useEffect(() => {
    if (socket) {
      socket.on('lead:updated', handleLeadUpdated);
      
      return () => {
        socket.off('lead:updated', handleLeadUpdated);
      };
    }
  }, [socket, lead.leadId, lead.nextFollowUpTime, localCallCount, handleLeadUpdated]);
  
  // Listen for call updates
  useEffect(() => {
    if (socket) {
      const handleCallAdded = (data) => {
        if (data.leadId === lead.leadId) {
          console.log('[LeadCard] Call added for this lead:', {
            leadId: data.leadId,
            callOutcome: data.outcome,
            timestamp: data.timestamp,
            serverCallCount: data.callCount, 
            currentLocalCallCount: localCallCount,
            nextFollowUpTime: data.nextFollowUpTime || data.rescheduledTo,
            currentFollowUpTime: lead.nextFollowUpTime
          });
          
          // Update local call count immediately for responsive UI
          if (data.callCount !== undefined && data.callCount !== null) {
            const newCount = Number(data.callCount);
            console.log('[LeadCard] Updating call count from call:added event:', {
              leadId: data.leadId,
              oldCount: localCallCount,
              newCount: newCount,
              timeUpdated: new Date().toLocaleTimeString()
            });
            setLocalCallCount(newCount);
          } else {
            // If server didn't provide a call count, increment the local one
            const newCount = localCallCount + 1;
            console.log('[LeadCard] Incrementing call count locally:', {
              leadId: data.leadId,
              oldCount: localCallCount,
              newCount: newCount,
              timeUpdated: new Date().toLocaleTimeString()
            });
            setLocalCallCount(newCount);
          }
          
          // Update last call time if provided
          if (data.timestamp) {
            setLocalLastCallTime(data.timestamp);
          }
          
          // Check for follow-up time updates (can be in nextFollowUpTime or rescheduledTo)
          const newFollowUpTime = data.nextFollowUpTime || data.rescheduledTo;
          if (newFollowUpTime) {
            console.log('[LeadCard] Detected follow-up time in call:added event:', {
              leadId: data.leadId,
              newFollowUpTime: newFollowUpTime,
              currentFollowUpTime: lead.nextFollowUpTime,
              currentLocalFollowUpTime: localNextFollowUpTime,
              formattedNew: new Date(newFollowUpTime).toLocaleString(),
              formattedCurrent: lead.nextFollowUpTime ? new Date(lead.nextFollowUpTime).toLocaleString() : 'none',
              timeUpdated: new Date().toLocaleTimeString(),
              needsUpdate: lead.nextFollowUpTime !== newFollowUpTime || localNextFollowUpTime !== newFollowUpTime
            });
            
            // Always update the local state immediately for responsive UI
            if (localNextFollowUpTime !== newFollowUpTime) {
              console.log('[LeadCard] Updating local follow-up time from call:added event:', {
                leadId: data.leadId,
                old: localNextFollowUpTime,
                new: newFollowUpTime,
                formattedOld: localNextFollowUpTime ? new Date(localNextFollowUpTime).toLocaleString() : 'none',
                formattedNew: new Date(newFollowUpTime).toLocaleString(),
                time: new Date().toLocaleTimeString()
              });
              setLocalNextFollowUpTime(newFollowUpTime);
              
              // Force re-render
              setRenderKey(Date.now());
            }
            
            // Directly fetch the latest lead data from the API to force an update
            if (lead.nextFollowUpTime !== newFollowUpTime) {
              console.log('[LeadCard] Follow-up time changed from call:added, fetching fresh data');
              
              // Fetch the latest lead data from the API
              (async () => {
                try {
                  const response = await api.get(`/api/leads/${lead.leadId}`);
                  console.log('[LeadCard] Received fresh lead data from API after call:', {
                    leadId: response.data.leadId,
                    nextFollowUpTime: response.data.nextFollowUpTime,
                    formattedTime: response.data.nextFollowUpTime ? new Date(response.data.nextFollowUpTime).toLocaleString() : 'none'
                  });
                  
                  // Emit a lead:updated event with the fresh data to ensure all components update
                  socket.emit('lead:updated', response.data);
                  
                  // If the API data has a different follow-up time, force a local update
                  if (response.data.nextFollowUpTime !== lead.nextFollowUpTime) {
                    // Create a forced update event that matches the lead:updated format
                    handleLeadUpdated({
                      leadId: lead.leadId,
                      nextFollowUpTime: response.data.nextFollowUpTime,
                      // Include other fields that might be in the lead:updated event
                      callCount: response.data.callCount || localCallCount,
                      lastCallTime: response.data.lastCallTime || localLastCallTime
                    });
                  }
                } catch (error) {
                  console.error('[LeadCard] Error fetching updated lead data after call:', error);
                }
              })();
            }
          }
        }
      };
      
      socket.on('call:added', handleCallAdded);

      return () => {
        socket.off('call:added', handleCallAdded);
      };
    }
  }, [socket, lead.leadId, lead.nextFollowUpTime, localCallCount, localLastCallTime, localNextFollowUpTime]);

  // Force re-render when nextFollowUpTime changes
  useEffect(() => {
    console.log('[LeadCard] nextFollowUpTime prop changed:', {
      leadId: lead.leadId,
      nextFollowUpTime: lead.nextFollowUpTime,
      formattedTime: lead.nextFollowUpTime ? new Date(lead.nextFollowUpTime).toLocaleString() : 'none',
      time: new Date().toLocaleTimeString()
    });
    
    // Update the render key to force a re-render
    setRenderKey(Date.now());
    
    // Call handleLeadUpdated to process any changes
    if (lead.nextFollowUpTime) {
      handleLeadUpdated({
        leadId: lead.leadId,
        nextFollowUpTime: lead.nextFollowUpTime
      });
    }
  }, [lead.nextFollowUpTime, lead.leadId, handleLeadUpdated]);

  useEffect(() => {
    if (lead.incompleteRequirements?.length > 0) {
      console.log('Lead card requirements:', lead.leadId, lead.incompleteRequirements);
    }
  }, [lead.incompleteRequirements, lead.leadId]);

  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 0) {
      // Future date
      const absDiffInSeconds = Math.abs(diffInSeconds);
      const absDiffInMinutes = Math.floor(absDiffInSeconds / 60);
      const absDiffInHours = Math.floor(absDiffInMinutes / 60);
      const absDiffInDays = Math.floor(absDiffInHours / 24);

      if (absDiffInDays > 0) return `in ${absDiffInDays} day${absDiffInDays > 1 ? 's' : ''}`;
      if (absDiffInHours > 0) return `in ${absDiffInHours} hour${absDiffInHours > 1 ? 's' : ''}`;
      if (absDiffInMinutes > 0) return `in ${absDiffInMinutes} minute${absDiffInMinutes > 1 ? 's' : ''}`;
      return 'in less than a minute';
    } else {
      // Past date
      if (diffInDays > 0) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
      if (diffInHours > 0) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
      if (diffInMinutes > 0) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
      return 'just now';
    }
  };

  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.100', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.600');

  const incompleteReqs = lead.incompleteRequirements || [];

  return (
    <>
      <Box
        key={`lead-card-${lead.leadId}-${renderKey}`}
        p={2}
        bg={bgColor}
        borderRadius="md"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
        _hover={{ 
          boxShadow: 'md',
          transform: 'translateY(-2px)',
          bg: hoverBgColor
        }}
        transition="all 0.2s"
        width="100%"
        cursor="grab"
        userSelect="none"
        _active={{ cursor: 'grabbing' }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onAdvancedEdit(lead);
        }}
      >
        <HStack justify="space-between" mb={1}>
          <Text 
            fontSize={fontSize.md}
            fontWeight="bold"
            color={lead.brideAndGroomName ? textColor : mutedTextColor}
          >
            {lead.brideAndGroomName || 'No Name Added'}
          </Text>
          <HStack spacing={2}>
            {incompleteReqs.length > 0 && (
              <Tooltip label={
                <Box p={1}>
                  <Text fontWeight="bold" mb={2} fontSize={fontSize.sm}>
                    Requirements for Next Stage:
                  </Text>
                  {incompleteReqs.map((req, index) => (
                    <Box key={index} mb={1}>
                      <Text mb={0.5} fontSize={fontSize.sm}>
                        • {req.name}
                      </Text>
                      <Text fontSize={fontSize.xs} mb={1} pl={3} color={mutedTextColor}>
                        {req.description || 'Required'}
                      </Text>
                      {req.missing?.length > 0 && (
                        <Text fontSize={fontSize.xs} pl={3} color="red.400">
                          Missing: {req.missing.map(field => {
                            switch(field) {
                              case 'events': return 'Event Details';
                              case 'guestCapacity': return 'Guest Capacity';
                              case 'budget.amount': return 'Budget Amount';
                              case 'locations': return 'Locations';
                              case 'phone': return 'Phone Number';
                              case 'brideAndGroomName': return 'Bride and Groom Names';
                              default: return field;
                            }
                          }).join(', ')}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Box>
              } placement="top-start" hasArrow>
                <IconButton
                  size="xs"
                  colorScheme="orange"
                  icon={<Icon as={FaExclamationTriangle} />}
                  aria-label="Incomplete requirements"
                  onClick={() => {
                    // You can add a modal or tooltip to show the requirements
                    console.log('Incomplete requirements:', incompleteReqs);
                  }}
                />
              </Tooltip>
            )}

            <Tooltip label="Record Call">
              <IconButton
                size="xs"
                icon={<FaPhone />}
                aria-label="Record call"
                onClick={onCallModalOpen}
                colorScheme="green"
              />
            </Tooltip>
            <Tooltip label="Advanced Edit">
              <IconButton
                size="xs"
                icon={<SettingsIcon />}
                aria-label="Advanced edit"
                onClick={() => onAdvancedEdit(lead)}
                colorScheme="blue"
              />
            </Tooltip>
          </HStack>
        </HStack>

        <VStack align="stretch" spacing={0.5}>
          {/* Phone number removed */}
          <Text fontSize={fontSize.xs} color={mutedTextColor}>
            Added {getRelativeTime(lead.dateAdded)}
          </Text>

          <HStack spacing={2}>
            {/* Force re-render when nextFollowUpTime changes by adding a key */}
            {console.log('[LeadCard] Rendering follow-up for lead:', {
              leadId: lead.leadId,
              propsFollowUpTime: lead.nextFollowUpTime,
              localFollowUpTime: localNextFollowUpTime,
              formattedProps: lead.nextFollowUpTime ? new Date(lead.nextFollowUpTime).toLocaleString() : 'none',
              formattedLocal: localNextFollowUpTime ? new Date(localNextFollowUpTime).toLocaleString() : 'none',
              renderTime: new Date().toLocaleTimeString()
            })}
            <Box key={`follow-up-${lead.leadId}-${localNextFollowUpTime || 'none'}-${Date.now()}`}>
              {localNextFollowUpTime ? (
                <Text 
                  fontSize={fontSize.xs} 
                  color={new Date(localNextFollowUpTime) < new Date() ? "red.500" : "blue.500"}
                >
                  Follow-up {getRelativeTime(localNextFollowUpTime)}
                </Text>
              ) : (
                <HStack spacing={0.5} color="red.500">
                  <Icon as={FaExclamationCircle} boxSize={3} />
                  <Text fontSize={fontSize.xs}>No next follow-up set</Text>
                </HStack>
              )}
            </Box>
            
            {/* Debug display to show the actual date */}
            {localNextFollowUpTime && (
              <Tooltip label={new Date(localNextFollowUpTime).toLocaleString()}>
                <InfoIcon color="gray.400" boxSize={3} />
              </Tooltip>
            )}
          </HStack>

          <HStack spacing={2} mt={1}>
            <HStack>
              <Icon as={FaComment} color="blue.500" boxSize={3} />
              <Text fontSize={fontSize.xs}>{lead.commentCount || 0}</Text>
            </HStack>
            
            <HStack>
              {(!localCallCount || localCallCount < 1) ? (
                <HStack spacing={0.5} color="yellow.500">
                  <Icon as={FaClock} boxSize={3} />
                  <Text fontSize={fontSize.xs}>No calls</Text>
                </HStack>
              ) : (
                <HStack>
                  <Icon as={FaPhone} color="green.500" boxSize={3} />
                  <Text fontSize={fontSize.xs}>{localCallCount}</Text>
                  {localLastCallTime && (
                    <Text fontSize={fontSize.xs} color="gray.500" ml={0.5}>
                      ({getRelativeTime(localLastCallTime)})
                    </Text>
                  )}
                </HStack>
              )}
            </HStack>
            
            <HStack>
              <Icon as={FaTasks} color="purple.500" />
              <Text fontSize={fontSize.sm}>{lead.taskCount || 0}</Text>
            </HStack>
          </HStack>

          {/* Tags */}
          {lead.tags?.length > 0 && (
            <HStack spacing={1} mt={1} wrap="wrap">
              {lead.tags.map((tag, index) => (
                <Badge
                  key={index}
                  fontSize={fontSize.xs}
                  variant="solid"
                  colorScheme={tag === 'Hot' ? 'red' : 
                                tag === 'Not Responding' ? 'yellow' : 
                                tag === 'Fake Lead' ? 'gray' : 
                                tag === 'Low Budget' ? 'orange' :
                                tag === 'Venues Pending' ? 'purple' :
                                tag === 'Proposal Pending' ? 'teal' :
                                tag === 'Discuss' ? 'cyan' :
                                'blue'}
                >
                  {tag}
                </Badge>
              ))}
            </HStack>
          )}
        </VStack>
      </Box>

      <RecordCallModal
        isOpen={isCallModalOpen}
        onClose={onCallModalClose}
        lead={lead}
        onSubmit={async (callData, leadId, leadUpdates) => {
          try {
            console.log('[LeadCard] Submitting call data:', {
              callData,
              leadId,
              leadUpdates
            });
            
            // First log the call
            await api.post('/api/call-logs', callData);

            // Update the lead's next follow-up time if needed
            if (leadUpdates && leadUpdates.nextFollowUpTime) {
              console.log('[LeadCard] Updating lead with nextFollowUpTime:', leadUpdates.nextFollowUpTime);
              await onEdit({
                ...lead,
                nextFollowUpTime: leadUpdates.nextFollowUpTime
              });
            } else if (callData.rescheduledTo) {
              // Fallback to old behavior for backward compatibility
              console.log('[LeadCard] Fallback: Updating lead with rescheduledTo:', callData.rescheduledTo);
              await onEdit({
                ...lead,
                nextFollowUpTime: callData.rescheduledTo
              });
            }
            
            // Fetch the updated lead data directly
            try {
              console.log('[LeadCard] Fetching updated lead data after call submission');
              const response = await api.get(`/api/leads/${lead.leadId}`);
              console.log('[LeadCard] Received updated lead data:', response.data);
              // Let the parent component handle the update through the onEdit callback
              if (response.data) {
                onEdit(response.data);
              }
            } catch (fetchError) {
              console.error('[LeadCard] Error fetching updated lead data:', fetchError);
            }

            // Close the call modal
            onCallModalClose();

            // Only for answered calls, open the edit modal
            if (callData.outcome === 'answered') {
              setTimeout(() => {
                onAdvancedEdit({
                  ...lead,
                  initialTab: 'basic'
                });
              }, 100); // Small delay to ensure modal closes first
            }
          } catch (error) {
            console.error('Error logging call:', error);
            toast({
              title: 'Error logging call',
              description: error.response?.data?.message || error.message || 'Something went wrong',
              status: 'error',
              duration: 5000,
            });
          }
        }}
      />
    </>
  );
};

// Use memo to prevent unnecessary re-renders
export default memo(LeadCard, (prevProps, nextProps) => {
  // Log when comparison happens for debugging
  console.log('[LeadCard] Comparing memoization for lead:', {
    leadId: nextProps.lead.leadId,
    prevFollowUpTime: prevProps.lead.nextFollowUpTime,
    nextFollowUpTime: nextProps.lead.nextFollowUpTime,
    timeCompared: new Date().toLocaleTimeString()
  });
  
  // Return false (meaning re-render) if any of these props change
  return (
    prevProps.lead.leadId === nextProps.lead.leadId &&
    // We need to compare nextFollowUpTime since we're now using it directly
    prevProps.lead.nextFollowUpTime === nextProps.lead.nextFollowUpTime &&
    // We don't compare callCount and lastCallTime here because we handle those with local state
    prevProps.lead.commentCount === nextProps.lead.commentCount &&
    prevProps.lead.stage === nextProps.lead.stage
  );
});
